import { useNavigate } from "react-router-dom";
import { PageContainer, SearchIcon, toast } from "@kkp/ui";
import { useCompaniesTable } from "@kkp/features";
import { Input } from "../../ui/Input";
import type { Company } from "../../api/companies";
import { useClientsData } from "./hooks/useClientsData";
import { useAddCompanyForm } from "./hooks/useAddCompanyForm";
import { useEditCompanyForm } from "./hooks/useEditCompanyForm";
import { AddCompanyDialog } from "./components/AddCompanyDialog";
import { ClientsStats } from "./components/ClientsStats";
import { ClientsTable } from "./components/ClientsTable";
import { EditCompanyDialog } from "./components/EditCompanyDialog";

export default function ClientsPage() {
  const navigate = useNavigate();
  const data = useClientsData();
  const table = useCompaniesTable(data.companies);
  const addForm = useAddCompanyForm({ onCreated: data.appendCompany });
  const editForm = useEditCompanyForm({ onUpdated: data.applyUpdate });

  const onRowAction = (
    action: "view" | "edit" | "toggleStatus",
    c: Company,
  ) => {
    if (action === "view") {
      navigate(`/company/${c.id}`);
      return;
    }
    if (action === "edit") {
      editForm.openEditDialog(c);
      return;
    }
    data.toggleStatus(c);
  };

  const onBulkAction = (action: "export" | "setActive" | "setInactive") => {
    if (table.selectedCount === 0) {
      toast.warning("Select at least one company.", {
        title: "Bulk actions",
        durationMs: 2400,
      });
      return;
    }
    const ids = Object.keys(table.selected).filter((id) => table.selected[id]);
    if (action === "export") {
      data.exportSelected(ids);
      return;
    }
    data.bulkSetStatus(ids, action === "setActive" ? "Active" : "Inactive");
  };

  return (
    <PageContainer
      title="Clients"
      description="Search, filter, and manage client companies."
      actions={
        <>
          <div className="w-full sm:w-[320px]">
            <Input
              value={table.query}
              onChange={(e) => table.setQuery(e.target.value)}
              placeholder="Search by name or ID…"
              leftIcon={<SearchIcon />}
              aria-label="Search companies"
            />
          </div>

          <AddCompanyDialog form={addForm} />
          <EditCompanyDialog form={editForm} />
        </>
      }
    >
      <div className="grid gap-4">
        <ClientsStats stats={table.stats} />
        <ClientsTable
          table={table}
          isLoading={data.isLoading}
          onRowAction={onRowAction}
          onBulkAction={onBulkAction}
        />
      </div>
    </PageContainer>
  );
}
